import { useState, useEffect } from 'react';
import { updateSessionData } from '../utils/cookies';

interface UTMData {
  utmTrackingId: number;
  shouldSendGA4: boolean;
  utmParams: Record<string, string | null>;
  isNew: boolean;
}

// Declare gtag function
declare function gtag(command: string, targetId: string, config: string, callback: (clientId: string) => void): void;

// Show a campaign banner from ast_promo URL param
async function activateAstPromoBanner(slug: string): Promise<void> {
  try {
    // Look up the campaign by slug to get banner config
    const res = await fetch(`/api/campaigns`);
    const data = await res.json();
    if (!data.success) return;
    const campaign = (data.campaigns as any[]).find(
      (c: any) => c.slug === slug && c.bannerEnabled && c.isActive
    );
    if (!campaign?.bannerText) return;

    // Inject banner using same pattern as utmPromoService
    const existing = document.getElementById('ast-promo-banner');
    if (existing) existing.remove();

    const el = document.createElement('div');
    el.id = 'ast-promo-banner';
    el.style.cssText = `position:fixed;top:0;left:0;right:0;z-index:9999;background:${campaign.bannerColor || '#1E40AF'};color:white;padding:12px 16px;text-align:center;font-family:system-ui,-apple-system,sans-serif;font-weight:600;box-shadow:0 2px 8px rgba(0,0,0,0.15);animation:astBannerIn 0.3s ease-out;`;

    if (!document.querySelector('#ast-promo-banner-styles')) {
      const style = document.createElement('style');
      style.id = 'ast-promo-banner-styles';
      style.textContent = '@keyframes astBannerIn{from{transform:translateY(-100%)}to{transform:translateY(0)}}@keyframes astBannerOut{from{transform:translateY(0)}to{transform:translateY(-100%)}}';
      document.head.appendChild(style);
    }

    el.innerHTML = `<div style="display:flex;justify-content:center;align-items:center;gap:10px;max-width:1200px;margin:0 auto"><span style="flex:1">${campaign.bannerText}</span><button onclick="this.closest('#ast-promo-banner').style.animation='astBannerOut 0.3s ease-out forwards';setTimeout(()=>this.closest('#ast-promo-banner').remove(),300)" style="background:rgba(255,255,255,0.2);border:none;color:white;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:1.1em">✕</button></div>`;
    document.body.appendChild(el);
    setTimeout(() => { if (el.parentElement) { el.style.animation = 'astBannerOut 0.3s ease-out forwards'; setTimeout(() => el.remove(), 300); } }, 15000);
    console.log('✅ ast_promo banner shown for campaign:', slug);
  } catch (e) {
    console.warn('ast_promo banner lookup failed:', e);
  }
}

// Show a campaign banner looked up by campaign_code (e.g. from URL hash like #WINTERWARMTH)
async function activateAstPromoByCode(code: string): Promise<void> {
  try {
    // Use the by-promo endpoint which normalises the code server-side
    const res = await fetch(`/api/campaigns/by-promo/${encodeURIComponent(code)}`);
    const data = await res.json();
    if (!data.success || !data.campaign) return;
    const campaign = data.campaign;
    if (!campaign.bannerEnabled || !campaign.isActive || !campaign.bannerText) return;
    // Render the banner directly — no second fetch needed
    const existing = document.getElementById('ast-promo-banner');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.id = 'ast-promo-banner';
    el.style.cssText = `position:fixed;top:0;left:0;right:0;z-index:9999;background:${campaign.bannerColor || '#1E40AF'};color:white;padding:12px 16px;text-align:center;font-family:system-ui,-apple-system,sans-serif;font-weight:600;box-shadow:0 2px 8px rgba(0,0,0,0.15);`;
    el.innerHTML = `<div style="display:flex;justify-content:center;align-items:center;gap:10px;max-width:1200px;margin:0 auto"><span style="flex:1">${campaign.bannerText}</span><button onclick="this.closest('#ast-promo-banner').style.opacity='0';setTimeout(()=>this.closest('#ast-promo-banner').remove(),300)" style="background:rgba(255,255,255,0.2);border:none;color:white;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:1.1em">✕</button></div>`;
    document.body.appendChild(el);
    setTimeout(() => { if (el.parentElement) el.remove(); }, 15000);
    console.log('✅ Hash promo banner shown:', campaign.name, '| code:', code);
  } catch (e) {
    console.warn('hash campaign banner lookup failed:', e);
  }
}

// Custom hook for UTM tracking
export const useUTMTracking = () => {
  const [utmData, setUTMData] = useState<UTMData | null>(null);
  const [ga4ClientId, setGA4ClientId] = useState<string | null>(null);

  useEffect(() => {
    // First check if any UTM parameters exist before doing anything
    const urlParams = new URLSearchParams(window.location.search);

    const utmParams: Record<string, string | null> = {
      gad_campaignid: urlParams.get('gad_campaignid'),
      gad_source: urlParams.get('gad_source'),
      gbraidid: urlParams.get('gbraidid'),
      gclid: urlParams.get('gclid'),
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_term: urlParams.get('utm_term'),
      utm_content: urlParams.get('utm_content'),
      fbclid: urlParams.get('fbclid'),
      ast_promo: urlParams.get('ast_promo'),
    };

    // Fallback for malformed redirects (e.g. WordPress redirecting to this widget with the
    // full destination URL — including its own query string — embedded as this URL's query
    // string: "?https://alpha-air.com.au/page/?utm_source=google&utm_medium=ads..."). In that
    // case URLSearchParams mangles whichever param is FIRST in the nested query string (its key
    // gets merged with everything before it), so it comes back null while every later param
    // still parses fine. Recover it with a direct regex pass over the full URL.
    const fallbackParamKeys: (keyof typeof utmParams)[] = [
      'gad_campaignid', 'gad_source', 'gbraidid', 'gclid',
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid',
    ];
    for (const key of fallbackParamKeys) {
      if (!utmParams[key]) {
        const match = window.location.href.match(new RegExp(`[?&]${key}=([^&#]+)`));
        if (match) {
          try {
            utmParams[key] = decodeURIComponent(match[1]);
          } catch {
            utmParams[key] = match[1];
          }
          console.log(`🩹 Recovered "${key}" via malformed-URL fallback:`, utmParams[key]);
        }
      }
    }

    // Trigger campaign banner if ast_promo param present
    const astPromo = urlParams.get('ast_promo');
    if (astPromo) {
      activateAstPromoBanner(astPromo);
    }

    // Trigger campaign banner from URL hash (e.g. #winterwarmth → campaign_code)
    const hashCode = window.location.hash.replace(/^#/, '').trim();
    if (hashCode && !astPromo) {
      activateAstPromoByCode(hashCode);
      // Also write hash into utm_content so it's recorded on the lead
      // (utm_campaign may already be set to a Facebook numeric ID from the ad)
      if (!utmParams.utm_content) {
        utmParams.utm_content = hashCode.toLowerCase();
      }
    }

    // Check if any UTM parameters exist OR if referred from alpha-air.com.au OR Podium link
    const hasUTMParams = Object.entries(utmParams).some(([k, v]) => k !== 'ast_promo' && v);
    const isAlphaAirReferral = document.referrer && document.referrer.includes('alpha-air.com.au');
    const isPodiumLink = urlParams.has('pm');

    // Debug logging to verify referrer detection
    console.log('🔍 Referrer Detection Debug:', {
      documentReferrer: document.referrer,
      isAlphaAirReferral,
      isPodiumLink,
      hasUTMParams,
      currentUrl: window.location.href
    });

    // Only proceed if UTM parameters exist OR referred from main website OR Podium link OR ast_promo
    if (!hasUTMParams && !isAlphaAirReferral && !isPodiumLink && !astPromo) {
      console.log('No UTM parameters, alpha-air.com.au referral, or Podium link found, skipping tracking');
      return;
    }

    // If referred from alpha-air.com.au but no UTM params, create synthetic UTM data
    if (isAlphaAirReferral && !hasUTMParams) {
      utmParams.utm_source = 'alpha-air-website';
      utmParams.utm_medium = 'referral';
      utmParams.utm_campaign = 'website-referral';
      console.log('✅ Alpha Air website referral detected, creating synthetic UTM data', {
        originalReferrer: document.referrer,
        syntheticUTM: utmParams
      });
    }

    // If Podium link (?pm) but no UTM params, create synthetic UTM data
    if (isPodiumLink && !hasUTMParams) {
      utmParams.utm_source = 'podium';
      utmParams.utm_medium = 'messaging';
      utmParams.utm_campaign = 'podium-link';
      console.log('✅ Podium link detected, creating synthetic UTM data');
    }

    console.log('UTM parameters found, initializing tracking:', utmParams);

    // Helper: detect if a value is a Meta/platform numeric ID (all digits)
    const isNumericId = (v: string | null) => !!v && /^\d+$/.test(v);

    // Capture UTM parameters from URL
    const captureUTMData = async () => {
      try {
        // If there's a hash promo code, look up the campaign and backfill UTM fields
        // that are missing or contain Meta's opaque numeric IDs
        if (hashCode) {
          try {
            const promoRes = await fetch(`/api/campaigns/by-promo/${encodeURIComponent(hashCode)}`);
            const promoData = await promoRes.json();
            if (promoData.success && promoData.campaign) {
              const c = promoData.campaign;
              // Backfill: use campaign values where the URL has nothing or a numeric Meta ID
              if (!utmParams.utm_source || isNumericId(utmParams.utm_source)) {
                utmParams.utm_source = c.utmSource || utmParams.utm_source;
              }
              if (!utmParams.utm_medium || isNumericId(utmParams.utm_medium)) {
                utmParams.utm_medium = c.utmMedium || utmParams.utm_medium;
              }
              if (!utmParams.utm_campaign || isNumericId(utmParams.utm_campaign)) {
                utmParams.utm_campaign = c.utmCampaign || c.slug || utmParams.utm_campaign;
              }
              // Always store the promo code so it's visible in tracking data
              (utmParams as any).promo_code = hashCode.toLowerCase();
              console.log('✅ Hash promo backfilled from campaign:', c.name, utmParams);
            }
          } catch (e) {
            console.warn('Promo campaign lookup failed:', e);
          }
        }

        // Send UTM data to backend for tracking
        const response = await fetch('/api/utm/capture', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            utmParams,
            pageUrl: window.location.href,
            referrer: document.referrer
          })
        });

        const result = await response.json();

        if (result.success) {
          setUTMData({
            utmTrackingId: result.utmTrackingId,
            shouldSendGA4: result.shouldSendGA4,
            utmParams,
            isNew: result.isNew
          });
          // Write UTM params back into session so ContactForm can read them
          updateSessionData({
            utmData: {
              source: utmParams.utm_source || undefined,
              medium: utmParams.utm_medium || undefined,
              campaign: utmParams.utm_campaign || undefined,
              content: utmParams.utm_content || undefined,
              term: utmParams.utm_term || undefined,
            }
          });
        }
      } catch (error) {
        console.error('Error capturing UTM data:', error);
      }
    };

    // Get GA4 Client ID (only when we have UTM params)
    const getGA4ClientId = () => {
      // Try to get from gtag if available
      if (typeof window !== 'undefined' && typeof gtag !== 'undefined') {
        gtag('get', process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-SBPK84T1KF', 'client_id', (clientId: string) => {
          setGA4ClientId(clientId);
        });
      } else {
        // Fallback: generate client ID
        const clientId = `${Date.now()}.${Math.random().toString(36).substr(2, 9)}`;
        setGA4ClientId(clientId);
      }
    };

    captureUTMData();
    getGA4ClientId();
  }, []);

  return { utmData, ga4ClientId };
};

export default useUTMTracking;
